import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import { supabase } from '@maestra/core/lib/supabase';
import { artistsActions } from '@maestra/core/store/slices/artists';
import { perfilEstaAtivo, useArtistCapabilities } from '@maestra/core/hooks/useArtistCapabilities';
import { useEntitlements } from '@maestra/core/hooks/useEntitlements';
import { useJourneyState } from '@maestra/core/hooks/useJourneyState';
import { pilaresDoPainel } from '@maestra/core/nucleo/pilaresDoPainel';
import { shouldEnrichChartmetric } from '@maestra/core/lib/chartmetricFreshness';
import { CHAMADA_DO_PLANEJAMENTO as CHAMADA_DA_JORNADA } from '@maestra/core/constants/checkout';
import { INCLUSOES_DO_DESBLOQUEIO, etapasDaJornada } from '@maestra/core/telas/dinheiro/jornadaDoDesbloqueio';
import { CABECALHO_DA_ENTREGA } from '@maestra/core/constants/realCopy';
import { CHAMADA_DO_PLANEJAMENTO, LEVAR_O_DIAGNOSTICO, METODOLOGIA, QUEM_ASSINA, VIDEO_DO_PLANEJAMENTO } from '@maestra/core/constants/realNarrative';
import { autoriaDoDocumento } from '@maestra/core/documentos/diagnostico';
import { fotoDoArtista } from '@maestra/core/nucleo/fotoDoArtista';

import { message } from '../../ui-carbon/padroes/avisos';
import { guardarModelo, lerModeloGuardado, type ModeloDoPdf } from '../pdf/modelos';
import { useCaminhoDoPro } from './caminhoDoPro';
import { relatorioReal, type Chartmetric } from '../desenvolvimento/relatorioReal';
import type { DadosDoDiagnostico } from '../desenvolvimento/useDiagnostico';

// O DESBLOQUEIO DE UM PERFIL (/artists/:id/desbloquear), sem tela. Cópia literal de
// `pages/ProfileUnlock`: a passagem do diagnóstico para o planejamento.
//  - as etapas (diagnóstico, jornada, checkout) moram na URL (`?ver=`), e trocar de etapa não
//    recarrega nada (o formulário meio preenchido não se perde);
//  - perfil já ativo não tem o que vender: volta para o perfil (menos na tela de sucesso, que segura
//    o redirect para o botão levar ao planejamento);
//  - o Pass Access é resgatado AQUI (quem tem um código não atravessa o pagamento), com o destrave
//    otimista e o enriquecimento da Chartmetric sem bloquear;
//  - o checkout é o mesmo da /planos;
//  - O CHECKOUT SÓ ABRE NO PLANO GRÁTIS (04/10/2026, `caminhoDoPro`): com o PIX já gerado e válido,
//    o botão retoma o pagamento (e não gera outro QR); quem já assina e tem o perfil fora da cota
//    inclui o perfil na assinatura (e não assina de novo); em atraso, regulariza.
// A jornada segue a ordem do CICLO DO PRODUTO (diagnóstico → planejamento → plano), e não a da home.

type Passo = 'diagnostico' | 'jornada' | 'checkout' | 'done';

// O QUE O PRO INCLUI, nomeando cada módulo. O cartão do núcleo juntava "Músicas, agenda e equipe"
// e deixava de fora o CRM e os Shows, que são metade do que se usa no dia a dia. A Nyta e o
// armazenamento seguem o texto do núcleo (a mesma promessa do app); os módulos são ditos um a um,
// com os dois do Controle de shows (no menu, Vendas e Produção) pelo que cada um faz.
// As inclusões do PRO e as etapas da jornada moram no núcleo desde 31/10/2026 (o app mostra as
// mesmas): `@maestra/core/telas/dinheiro/jornadaDoDesbloqueio`.
export { INCLUSOES_DO_DESBLOQUEIO };

export const useDesbloqueio = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = useParams();
  const user = useAppSelector((s) => s.auth.user);
  const artist = useAppSelector((s) => s.artists.items.find((a) => a.id === id));
  const loaded = useAppSelector((s) => s.artists.loaded);
  const { isPro } = useEntitlements();

  const jornada = useJourneyState(artist);
  const capacidades = useArtistCapabilities(artist);
  const caminho = useCaminhoDoPro(artist);
  const pilares = pilaresDoPainel(artist, jornada, capacidades, { portaDeLeitura: true });

  const [step, setStep] = useState<Passo>(
    searchParams.get('ver') === 'diagnostico'
      || (location.state as { showDiagnostic?: boolean } | null)?.showDiagnostic
      ? 'diagnostico'
      // O código de acesso mora no pagamento (04/10/2026): quem veio por "Tenho um código de
      // acesso" cai direto nele, com o campo em foco.
      : searchParams.get('ver') === 'checkout' || (location.state as { codigo?: boolean } | null)?.codigo ? 'checkout' : 'jornada',
  );
  const [codigo, setCodigo] = useState('');
  const [resgatando, setResgatando] = useState(false);
  const [erroDoCodigo, setErroDoCodigo] = useState('');
  const [baixando, setBaixando] = useState(false);
  // O banco de modelos do PDF (o mesmo do branding): o modal e o modelo escolhido, guardado no navegador.
  const [pdfAberto, setPdfAberto] = useState(false);
  const [modeloDoPdf, setModeloDoPdf] = useState<ModeloDoPdf>(() => lerModeloGuardado('diagnostico'));

  const irPara = useCallback((destino: Passo) => {
    setStep(destino);
    if (destino === 'done') return;
    setSearchParams((atual) => {
      const proximo = new URLSearchParams(atual);
      if (destino === 'jornada') proximo.delete('ver');
      else proximo.set('ver', destino);
      return proximo;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (!loaded && user?.id) dispatch(artistsActions.fetchArtists(user.id));
  }, [loaded, user?.id, dispatch]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, [step]);

  useEffect(() => {
    if (!loaded || step === 'done') return;
    if (!artist) navigate('/artists', { replace: true });
    else if (perfilEstaAtivo(artist, isPro)) navigate(`/artists/${id}`, { replace: true });
  }, [loaded, artist, id, navigate, step, isPro]);

  const realIndex = artist?.content?.realIndex ?? null;
  const cm = artist?.content?.chartmetricProfile;
  const chartmetric = (cm as Chartmetric | null | undefined) ?? null;
  // Sem Spotify não há dado de plataforma: a entrega não promete o que não foi medido.
  const noSpotify = !artist?.content?.spotifyProfile?.spotify_artist_id;

  const concluir = async (artistId: string) => {
    // O código libera o PLANEJAMENTO (e não o perfil inteiro): nada de marcar o perfil como pago
    // aqui, a lista relida traz a marca `liberado_por_codigo` que abre o wizard.
    if (user?.id) await dispatch(artistsActions.fetchArtists(user.id));
    setStep('done');
    if (shouldEnrichChartmetric(artist?.content?.chartmetricProfile)) {
      supabase.functions.invoke('artist-enrich-chartmetric', { body: { artistId } }).catch(() => {});
    }
  };

  const resgatarCodigo = async () => {
    if (!id) return;
    const code = codigo.trim();
    if (!code) return;
    setResgatando(true);
    setErroDoCodigo('');
    try {
      const { data } = await supabase.functions.invoke('redeem-access-pass', { body: { code, artistId: id } });
      if (data?.ok) { await concluir(id); return; }
      setErroDoCodigo(data?.error || 'Código inválido ou já utilizado.');
    } catch {
      setErroDoCodigo('Não foi possível validar o código. Tente de novo.');
    } finally {
      setResgatando(false);
    }
  };

  const relatorio = useMemo(() => (realIndex ? relatorioReal(realIndex, chartmetric) : null), [realIndex, chartmetric]);
  const meta = (user?.user_metadata ?? {}) as { full_name?: string; name?: string };
  const baixarPdf = async () => {
    if (!artist || !realIndex || baixando) return;
    setBaixando(true);
    try {
      const autoria = autoriaDoDocumento({
        email: user?.email, nome: meta.full_name || meta.name, artistId: artist.id,
        calculadoEm: String((realIndex as { computedAt?: string }).computedAt ?? ''),
        vinculo: (artist.content as { titularidade?: { vinculo?: string } } | undefined)?.titularidade?.vinculo,
      });
      const { baixarPdfDoDiagnostico } = await import('../desenvolvimento/pdfDoDiagnostico');
      await baixarPdfDoDiagnostico({ realIndex, chartmetric: chartmetric as never, nome: artist.name, foto: fotoDoArtista(artist), autoria, modelo: modeloDoPdf });
      setPdfAberto(false);
    } catch {
      message.error('Não foi possível gerar o PDF agora. Tente novamente.');
    } finally {
      setBaixando(false);
    }
  };

  const diagnostico: DadosDoDiagnostico | null = relatorio ? {
    carregando: false,
    naoAchou: false,
    nome: artist?.name ?? '',
    relatorio,
    refazer: null,
    pdf: {
      rotulo: baixando ? LEVAR_O_DIAGNOSTICO.baixando : LEVAR_O_DIAGNOSTICO.baixar, baixando, baixar: () => { void baixarPdf(); },
      aberto: pdfAberto, abrir: () => setPdfAberto(true), fechar: () => { if (!baixando) setPdfAberto(false); },
      modelo: modeloDoPdf, escolher: (m: ModeloDoPdf) => { setModeloDoPdf(m); guardarModelo('diagnostico', m); },
    },
    // A chamada leva à jornada (o próximo passo desta tela), e não ao wizard.
    planejamento: {
      titulo: CHAMADA_DO_PLANEJAMENTO.titulo,
      apoio: CHAMADA_DO_PLANEJAMENTO.apoio,
      nota: CHAMADA_DO_PLANEJAMENTO.nota,
      video: { url: VIDEO_DO_PLANEJAMENTO.url(VIDEO_DO_PLANEJAMENTO.id), titulo: VIDEO_DO_PLANEJAMENTO.titulo },
      botao: CHAMADA_DO_PLANEJAMENTO.botao,
      abrir: () => irPara('jornada'),
    },
    assinatura: { nome: QUEM_ASSINA.name, papel: QUEM_ASSINA.role, paragrafos: QUEM_ASSINA.paras as string[], destaque: QUEM_ASSINA.highlight },
    metodologia: {
      titulo: METODOLOGIA.title,
      intro: METODOLOGIA.intro as string[],
      dimensoes: (METODOLOGIA.dims as { l: string; t: string; d: string }[]).map((d) => ({ letra: d.l, titulo: d.t, texto: d.d })),
      fim: METODOLOGIA.outro,
    },
  } : null;

  return {
    estado: !loaded || !artist ? 'carregando' as const : step,
    nome: artist?.name ?? '',
    sair: () => navigate('/artists'),
    // As etapas do fluxo; a já feita é o caminho de volta (o diagnóstico só quando há um).
    etapa: step === 'diagnostico' ? 0 : step === 'jornada' ? 1 : 2,
    etapas: [
      { rotulo: 'Diagnóstico REAL', voltar: realIndex ? () => irPara('diagnostico') : null },
      { rotulo: 'Planejamento', voltar: () => irPara('jornada') },
      { rotulo: 'Pagamento', voltar: null },
    ],
    diagnostico: {
      cabecalho: { titulo: CABECALHO_DA_ENTREGA.titulo(artist?.name), apoio: noSpotify ? CABECALHO_DA_ENTREGA.apoioSemSpotify : CABECALHO_DA_ENTREGA.apoio },
      dados: diagnostico,
      continuar: () => irPara('jornada'),
    },
    jornada: {
      titulo: CHAMADA_DA_JORNADA.titulo(artist?.name),
      apoio: CHAMADA_DA_JORNADA.apoio,
      pilares: etapasDaJornada(pilares, id ? () => navigate(`/artists/${id}`) : null),
      inclusoes: INCLUSOES_DO_DESBLOQUEIO.map((x) => ({ chave: x.chave, titulo: x.titulo, linha: x.linha })),
      // Fora do plano grátis, o botão vai ao caminho da situação (retomar, incluir, regularizar).
      desbloquear: () => (caminho.especial ? caminho.ir() : irPara('checkout')),
      // O nome do botão diz o que a pessoa ganha, e não o que a tela faz com o perfil.
      acao: caminho.rotulo('Criar planejamento'),
      // Já liberado por código, o campo some: o que falta são os módulos, que vêm na assinatura.
      codigo: artist?.liberado_por_codigo ? null : {
        texto: CHAMADA_DA_JORNADA.codigo,
        valor: codigo,
        mudar: (v: string) => { setCodigo(v.toUpperCase()); setErroDoCodigo(''); },
        usar: () => { void resgatarCodigo(); },
        validando: resgatando,
        erro: erroDoCodigo || null,
        // Quem veio pelo "Tenho um código de acesso" chega com o campo já em foco.
        focar: !!(location.state as { codigo?: boolean } | null)?.codigo,
      },
    },
    checkout: {
      // O título diz o que a pessoa ganha (o planejamento), e não "desbloquear" (04/10/2026).
      titulo: `Assine o Maestra PRO e crie o planejamento${artist?.name ? ` de ${artist.name}` : ' estratégico'}`,
      apoio: 'O perfil entra na assinatura Maestra PRO, e o planejamento com a Nyta abre assim que o pagamento for confirmado.',
      aoAtivar: () => { if (id) navigate(`/artists/${id}/wizard`, { replace: true }); },
      aoIrParaPagamento: () => navigate('/pagamento'),
      aoJaSerAssinante: () => { if (id) navigate(`/artists/${id}`, { replace: true }); },
      /**
       * Fora do plano grátis, a etapa do pagamento não abre um checkout novo: diz a situação e leva
       * ao caminho dela (quem chega pelo endereço ?ver=checkout também cai aqui).
       */
      situacao: caminho.especial ? {
        titulo: caminho.especial.titulo,
        texto: caminho.especial.texto,
        rotulo: caminho.especial.rotulo,
        ir: () => caminho.ir(),
        // A outra saída. Pendente, é alterar o plano ou a forma de pagamento (a confirmação abre
        // nos Planos); "Ver assinatura" levava às Configurações, que não tinham nada a dizer ali
        // (04/10/2026). Fora da cota ou em atraso, a assinatura existe, e as Configurações a mostram.
        outra: caminho.situacao === 'pendente'
          ? { rotulo: 'Alterar plano ou pagamento', ir: () => navigate('/planos?alterar=1') }
          : { rotulo: 'Ver assinatura', ir: () => navigate('/settings') },
        // Com o pagamento pendente, o módulo travado oferece "Tenho um código de acesso" e traz
        // para cá: o campo do código precisa estar aqui também (QA do pagamento, 04/10/2026).
        comCodigo: caminho.situacao === 'pendente',
      } : null,
    },
    sucesso: {
      titulo: 'Pass Access confirmado',
      subtitulo: 'Seu planejamento estratégico está liberado.',
      texto: 'A Nyta já vai te guiar, passo a passo, na construção do seu plano.',
      botao: 'Iniciar planejamento',
      seguir: () => { if (id) navigate(`/artists/${id}/wizard`, { replace: true }); },
    },
  };
};

export type DadosDoDesbloqueio = ReturnType<typeof useDesbloqueio>;
